/**
 * General cleanup service that resets EVERYTHING without tracking individual items
 */
class CleanupService {
    // Store WebSocket connection reference globally for cleanup
    static websocketConnection = null;
    
    /**
     * Register the WebSocket connection for cleanup
     */
    static registerWebSocket(connection) {
        this.websocketConnection = connection;
    }
    
    /**
     * Clear WebSocket connection
     */
    static async clearWebSocket() {
        if (this.websocketConnection) {
            try {
                await this.websocketConnection.stop();
                this.websocketConnection = null;
            } catch (e) {
                console.warn('Could not stop WebSocket:', e);
                this.websocketConnection = null;
            }
        }
    }
    
    /**
     * Clear all browser storage
     */
    static clearAllStorage() {
        try {
            localStorage.clear();
            sessionStorage.clear();
        } catch (e) {
            console.warn('Could not clear storage:', e);
        }
    }
    
    /**
     * Clear all browser caches
     */
    static async clearAllCaches() {
        if ('caches' in window) {
            try {
                const names = await caches.keys();
                await Promise.all(names.map(name => caches.delete(name)));
            } catch (e) {
                console.warn('Could not clear caches:', e);
            }
        }
    }
    
    /**
     * Clear axios authorization headers
     */
    static clearAxiosAuth() {
        try {
            // Dynamic import to avoid circular dependencies
            const { SrxApi } = require('../api/SrxApi');
            const { SrxFsApi } = require('../api/SrxFsApi');
            
            SrxApi.defaults.headers.common['Authorization'] = undefined;
            SrxFsApi.defaults.headers.common['Authorization'] = undefined;
            
            // Also clear any cached requests
            delete SrxApi.defaults.headers.common;
            SrxApi.defaults.headers.common = {};
            delete SrxFsApi.defaults.headers.common;
            SrxFsApi.defaults.headers.common = {};
        } catch (e) {
            console.warn('Could not clear axios auth:', e);
        }
    }
    
    /**
     * MASTER CLEANUP - Call this on logout AND before login
     * Resets EVERYTHING to a fresh state
     */
    static async performFullCleanup(resetAtomsCallback) {
        // 1. Clear WebSocket connection FIRST (before clearing tokens)
        await this.clearWebSocket();
        
        // 2. Clear all storage
        this.clearAllStorage();
        
        // 3. Clear all caches
        await this.clearAllCaches();
        
        // 4. Clear axios
        this.clearAxiosAuth();
        
        // 5. Reset all Jotai atoms
        if (resetAtomsCallback && typeof resetAtomsCallback === 'function') {
            resetAtomsCallback();
        }
    }
    
    /**
     * Synchronous version for cases where you can't use async
     */
    static performFullCleanupSync(resetAtomsCallback) {
        // Clear WebSocket async but don't wait
        this.clearWebSocket().catch(e => console.warn('WebSocket clear failed:', e));
        
        this.clearAllStorage();
        this.clearAxiosAuth();
        
        if (resetAtomsCallback && typeof resetAtomsCallback === 'function') {
            resetAtomsCallback();
        }
        
        // Clear caches async but don't wait
        this.clearAllCaches().catch(e => console.warn('Cache clear failed:', e));
    }
}

export default CleanupService;
